import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, X, FileCheck2, GitPullRequestArrow, Paperclip, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Modal, useToast } from "@/components/ui";
import { Field, TextInput, TextArea, Select } from "@/components/fields";
import { fmtDateTime, money } from "@/lib/format";
import type { Approval } from "@shared/types";

const STATUS_CHIP: Record<string, string> = {
  pending: "bg-accent/15 text-accent",
  approved: "bg-st-ontrack/15 text-st-ontrack",
  rejected: "bg-st-delayed/15 text-st-delayed",
  withdrawn: "bg-surface-2 text-ink-faint",
};

export function ApprovalCard({ a, onChanged, canDecide }: { a: Approval; onChanged: () => void; canDecide: boolean }) {
  const { user } = useAuth();
  const toast = useToast();
  const [note, setNote] = useState("");
  const [comment, setComment] = useState("");
  const [expanded, setExpanded] = useState(false);

  const decide = useMutation({
    mutationFn: (decision: "approved" | "rejected") => api.post(`/approvals/${a.id}/decide`, { decision, note }),
    onSuccess: () => { toast.push("success", "Decision recorded"); onChanged(); },
    onError: (e: any) => toast.push("error", e?.message || "Failed"),
  });
  const withdraw = useMutation({
    mutationFn: () => api.post(`/approvals/${a.id}/withdraw`),
    onSuccess: () => { toast.push("success", "Withdrawn"); onChanged(); },
  });
  const addComment = useMutation({
    mutationFn: () => api.post(`/approvals/${a.id}/comments`, { body: comment }),
    onSuccess: () => { setComment(""); toast.push("success", "Comment added"); onChanged(); },
  });

  const Icon = a.type === "deliverable" ? FileCheck2 : GitPullRequestArrow;
  const isPending = a.status === "pending";
  const canAct = canDecide && isPending && (a.assignedToId === user?.id || user?.isSuperAdmin || canDecide);

  return (
    <div className="card p-4">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 rounded-lg bg-brand-50 p-2 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300"><Icon size={16} /></span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-medium">{a.title}</h3>
            <span className={`chip ${STATUS_CHIP[a.status]}`}>{a.status}</span>
            <span className="chip bg-surface-2 text-ink-soft capitalize">{a.type.replace("_", " ")}</span>
          </div>
          <p className="mt-0.5 text-xs text-ink-faint">
            {a.projectName}{a.activityTitle ? ` · ${a.activityTitle}` : ""} · raised by {a.requestedByName} · {fmtDateTime(a.createdAt)}
          </p>
          {a.description && <p className="mt-2 whitespace-pre-wrap text-sm text-ink-soft">{a.description}</p>}

          {a.type === "change_request" && (a.crImpactSchedule || a.crImpactCost != null || a.crImpactScope) && (
            <div className="mt-2 grid gap-1 rounded-lg bg-surface-2 p-2.5 text-xs sm:grid-cols-3">
              {a.crImpactSchedule && <span><b>Schedule impact:</b> {a.crImpactSchedule}</span>}
              {a.crImpactCost != null && <span><b>Cost impact:</b> {money(a.crImpactCost)}</span>}
              {a.crImpactScope && <span><b>Scope impact:</b> {a.crImpactScope}</span>}
            </div>
          )}

          {a.attachments.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {a.attachments.map((att) => (
                <a key={att.id} href={`${att.url}?dl=1`} className="chip border border-line hover:bg-surface-2">
                  <Paperclip size={12} /> {att.filename}
                </a>
              ))}
            </div>
          )}

          {a.status !== "pending" && (
            <p className="mt-2 rounded-lg bg-surface-2 px-3 py-2 text-xs">
              <b className="capitalize">{a.status}</b> by {a.decidedByName} · {fmtDateTime(a.decidedAt)}
              {a.decisionNote && <span className="mt-1 block text-ink-soft">"{a.decisionNote}"</span>}
            </p>
          )}

          {(a.comments.length > 0 || expanded) && (
            <div className="mt-3 space-y-2 border-l-2 border-line pl-3">
              {a.comments.map((c) => (
                <div key={c.id} className="text-xs">
                  <span className="font-medium">{c.authorName}</span> <span className="text-ink-faint">{fmtDateTime(c.createdAt)}</span>
                  <p className="text-ink-soft">{c.body}</p>
                </div>
              ))}
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {canAct && (
              <>
                <input className="input !w-64" placeholder="Decision note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
                <button className="btn bg-st-ontrack/15 text-st-ontrack hover:bg-st-ontrack/25" disabled={decide.isPending} onClick={() => decide.mutate("approved")}>
                  <Check size={15} /> Approve
                </button>
                <button className="btn-danger" disabled={decide.isPending} onClick={() => decide.mutate("rejected")}>
                  <X size={15} /> Reject
                </button>
              </>
            )}
            {isPending && a.requestedById === user?.id && (
              <button className="btn-ghost text-xs" onClick={() => withdraw.mutate()}>Withdraw</button>
            )}
            <button className="btn-ghost text-xs" onClick={() => setExpanded((e) => !e)}><MessageSquare size={13} /> Comment</button>
          </div>
          {expanded && (
            <div className="mt-2 flex gap-2">
              <input className="input" placeholder="Add a comment…" value={comment} onChange={(e) => setComment(e.target.value)} />
              <button className="btn-outline" disabled={!comment || addComment.isPending} onClick={() => addComment.mutate()}>Send</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function RaiseApprovalModal({ projectId, activityId, activityTitle, ownerName, onClose }: {
  projectId: string; activityId?: string; activityTitle?: string; ownerName?: string; onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [f, setF] = useState({
    type: "deliverable" as "deliverable" | "change_request",
    title: activityTitle ? `Deliverable sign-off: ${activityTitle}` : "",
    description: "", crImpactSchedule: "", crImpactCost: "", crImpactScope: "",
  });
  const [file, setFile] = useState<File | null>(null);

  const submit = useMutation({
    mutationFn: async () => {
      let attachmentIds: string[] = [];
      if (file) {
        const fd = new FormData();
        fd.append("file", file); fd.append("projectId", projectId); fd.append("kind", f.type);
        if (activityId) fd.append("activityId", activityId);
        const up = await api.upload<{ id: string }>("/attachments", fd);
        attachmentIds = [up.id];
      }
      return api.post("/approvals", {
        projectId, activityId, type: f.type, title: f.title, description: f.description,
        crImpactSchedule: f.crImpactSchedule || undefined,
        crImpactCost: f.crImpactCost ? Number(f.crImpactCost) : undefined,
        crImpactScope: f.crImpactScope || undefined,
        attachmentIds,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["approvals"] });
      toast.push("success", "Sent for approval");
      onClose();
    },
    onError: (e: any) => toast.push("error", e?.message || "Failed"),
  });

  return (
    <Modal open onClose={onClose} title="Raise for approval" wide
      footer={<>
        <button className="btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!f.title || submit.isPending} onClick={() => submit.mutate()}>Send{ownerName ? ` to ${ownerName}` : ""}</button>
      </>}>
      <div className="space-y-3.5">
        <Field label="Type">
          <Select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value as any })}>
            <option value="deliverable">Deliverable sign-off</option>
            <option value="change_request">Change request</option>
          </Select>
        </Field>
        <Field label="Title" required><TextInput value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
        <Field label="Description"><TextArea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
        {f.type === "change_request" && (
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Schedule impact"><TextInput value={f.crImpactSchedule} onChange={(e) => setF({ ...f, crImpactSchedule: e.target.value })} placeholder="+2 weeks" /></Field>
            <Field label="Cost impact"><TextInput type="number" value={f.crImpactCost} onChange={(e) => setF({ ...f, crImpactCost: e.target.value })} /></Field>
            <Field label="Scope impact"><TextInput value={f.crImpactScope} onChange={(e) => setF({ ...f, crImpactScope: e.target.value })} /></Field>
          </div>
        )}
        <Field label="Attach a document (optional)">
          <input type="file" className="text-sm" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </Field>
      </div>
    </Modal>
  );
}
